import { useSyncExternalStore } from 'react';

import { clearBrandColors } from '@/features/organization/brandTheme';

const STORAGE_KEY = 'edify_session';

/** Bump when the stored shape changes. A session from before is not narrowed to
 *  whatever still parses — it is dropped, and the user signs in again. */
const SESSION_VERSION = 2;

/**
 * Why the last session ended, so a page that lands on sign-in can say so.
 *
 * `requested` is the only one nobody has to be told about — it is the sign-out
 * button. Every other value is the app ending a session on the user's behalf,
 * which without a word looks like a sign-in button that does nothing.
 */
export type SessionEnd = 'requested' | 'expired' | 'denied' | 'revoked';

export interface SessionConfig {
  /** Run when a session ends, for state held outside this store. */
  onSessionEnd?: () => void;
}

let config: SessionConfig = {};

export function configureSession(next: SessionConfig) {
  config = next;
}

export type UserRole = 'admin' | 'member';

export interface Membership {
  organizationId: string;
  role: UserRole;
  permissions: string[];
}

export interface Session {
  token: string;
  name: string;
  email: string;
  organizationId: string | null;
  memberships: Membership[];
}

function isRole(value: unknown): value is UserRole {
  return value === 'admin' || value === 'member';
}

/** Keeps every string: the permission set is the backend's, and a copy of it
 *  here would drop whatever a user holds the moment the two disagree. */
export function parsePermissions(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item !== '');
}

function parseMembership(value: unknown): Membership | null {
  if (typeof value !== 'object' || value === null) return null;
  const { organization_id: organizationId, role, permissions } = value as Record<string, unknown>;
  if (typeof organizationId !== 'string' || !organizationId) return null;
  if (!isRole(role)) return null;
  return { organizationId, role, permissions: parsePermissions(permissions) };
}

function persist(session: Session): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...session, version: SESSION_VERSION }));
}

/** Reads the stored session, refusing one written under an older shape. */
function parseSession(raw: string | null): Session | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null) return null;
    const { version, token, name, email, organizationId, memberships } = value as Record<
      string,
      unknown
    >;
    if (version !== SESSION_VERSION) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (typeof token !== 'string' || !token) return null;
    if (typeof name !== 'string' || typeof email !== 'string') return null;
    if (organizationId !== null && typeof organizationId !== 'string') return null;
    if (!Array.isArray(memberships)) return null;
    return {
      token,
      name,
      email,
      organizationId: organizationId ?? null,
      memberships: memberships
        .map((entry) => parseMembership(toWire(entry)))
        .filter((entry): entry is Membership => entry !== null),
    };
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

/** Stored memberships are camelCase; the wire is snake_case. One parser reads both. */
function toWire(entry: unknown): unknown {
  if (typeof entry !== 'object' || entry === null) return entry;
  const record = entry as Record<string, unknown>;
  return { ...record, organization_id: record.organization_id ?? record.organizationId };
}

let current: Session | null = parseSession(localStorage.getItem(STORAGE_KEY));
let endedBecause: SessionEnd | null = null;
const listeners = new Set<() => void>();

function emit() {
  // A copy, because a notified listener can unmount the component that holds
  // another one — mutating the set mid-iteration silently skips it.
  for (const listener of [...listeners]) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY) return;
    current = parseSession(event.newValue);
    listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

export function signIn(session: Session) {
  current = session;
  endedBecause = null;
  persist(session);
  emit();
}

export function signOut(reason: SessionEnd = 'requested') {
  current = null;
  endedBecause = reason;
  localStorage.removeItem(STORAGE_KEY);
  clearBrandColors();
  config.onSessionEnd?.();
  emit();
}

export function updateMemberships(memberships: Membership[]): void {
  if (!current) return;
  if (JSON.stringify(current.memberships) === JSON.stringify(memberships)) return;
  current = { ...current, memberships };
  persist(current);
  emit();
}

export function updateToken(token: string): void {
  if (!current || current.token === token) return;
  current = { ...current, token };
  persist(current);
  emit();
}

export function getAuthToken(): string | null {
  return current?.token ?? null;
}

/** What ended the last session. Null once a new one starts. */
export function useSessionEnd(): SessionEnd | null {
  return useSyncExternalStore(
    subscribe,
    () => endedBecause,
    () => null,
  );
}

export function useSession(): Session | null {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => null,
  );
}

/** The organization every Firestore path and every API call is scoped to. */
export function useActiveOrganizationId(): string | null {
  return useSession()?.organizationId ?? null;
}

export function activeMembership(session: Session | null): Membership | null {
  if (!session?.organizationId) return null;
  return (
    session.memberships.find((entry) => entry.organizationId === session.organizationId) ?? null
  );
}

export function useActiveMembership(): Membership | null {
  return activeMembership(useSession());
}
